import { isValid, parseISO } from 'date-fns';

// Villa dates (prices, availability, deals, options) follow the calendar of Rome. These
// helpers use Intl, which ships with the browser, so no timezone database goes into the
// bundle. Days travel as 'yyyy-MM-dd' keys, compared as strings and moved with UTC day
// arithmetic, so DST and the visitor's timezone never shift them.

/** Calendar day as 'yyyy-MM-dd'. */
export type DayKey = string;

type RomeWallClock = {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

const romeFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Rome',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const pad = (value: number) => String(value).padStart(2, '0');

const formatDayKey = (year: number, month: number, day: number): DayKey =>
  `${year}-${pad(month)}-${pad(day)}`;

/** [year, month 1-12, day] of a day key. */
const splitDayKey = (key: DayKey) => key.split('-').map(Number);

// Rome changes offset on a whole UTC hour (01:00 UTC), so one Intl lookup per UTC hour is
// enough; calendars ask for the same hours over and over
const offsetByHour = new Map<number, number>();

function lookUpRomeOffsetMs(hourStart: number) {
  const parts = romeFormat.formatToParts(new Date(hourStart));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)?.value);
  const wallAsUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second'),
  );

  return wallAsUtc - hourStart;
}

/** Offset of Rome from UTC at an instant, in ms (+1h or +2h). */
function romeOffsetMs(epoch: number) {
  const hour = Math.floor(epoch / HOUR_MS);
  let offset = offsetByHour.get(hour);
  if (offset === undefined) {
    offset = lookUpRomeOffsetMs(hour * HOUR_MS);
    if (offsetByHour.size > 10_000) offsetByHour.clear();
    offsetByHour.set(hour, offset);
  }

  return offset;
}

/** Wall clock of Rome at an instant. */
export function romeWallClock(epoch: number = Date.now()): RomeWallClock {
  const wall = new Date(epoch + romeOffsetMs(epoch));

  return {
    year: wall.getUTCFullYear(),
    month: wall.getUTCMonth() + 1,
    day: wall.getUTCDate(),
    hour: wall.getUTCHours(),
    minute: wall.getUTCMinutes(),
    second: wall.getUTCSeconds(),
    millisecond: wall.getUTCMilliseconds(),
  };
}

/**
 * Instant of a Rome wall-clock time. Around a DST change:
 * - built from scratch: a repeated time takes its first (summer) occurrence and a skipped
 *   time moves forward by the gap;
 * - moved from another instant (`keepOffset`): the previous offset is kept when it is still
 *   valid, otherwise the other one is used.
 */
function wallToEpoch(wall: RomeWallClock, keepOffset?: number) {
  const wallAsUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
    wall.millisecond,
  );
  const offsetBefore = romeOffsetMs(wallAsUtc - DAY_MS);
  const offsetAfter = romeOffsetMs(wallAsUtc + DAY_MS);

  const offsets = [offsetBefore, offsetAfter];
  const valid = offsets.filter(
    (offset) => romeOffsetMs(wallAsUtc - offset) === offset,
  );

  if (keepOffset !== undefined) {
    if (valid.includes(keepOffset)) return wallAsUtc - keepOffset;
    if (valid.length) return wallAsUtc - valid[0];
    // Skipped hour: the offset that was not in use
    return (
      wallAsUtc -
      (offsets.find((offset) => offset !== keepOffset) ?? keepOffset)
    );
  }

  if (valid.length) return wallAsUtc - Math.max(...valid);
  // Skipped hour: forward by the gap (the winter offset)
  return wallAsUtc - Math.min(...offsets);
}

/**
 * Parses an API or URL date string: ISO with an offset is that instant, ISO without one is
 * local time. Anything else goes through Date.
 */
export function parseDateString(value: string) {
  const iso = parseISO(value);

  return isValid(iso) ? iso : new Date(value);
}

/** Instant of an API date, typed Date in places but a string at runtime. */
export function apiDateMs(value: Date | string) {
  return value instanceof Date
    ? value.getTime()
    : parseDateString(value).getTime();
}

// An offset only counts after a time ('...T10:00:00+02:00'), not the day of '2026-12-01'
const hasTimeOffset = (value: string) =>
  /\d{2}:\d{2}(?::\d{2}(?:[.,]\d+)?)?\s*(?:[zZ]|[+-]\d{2}(?::?\d{2})?)$/.test(
    value.trim(),
  );

/**
 * Parses a date string as UTC when it carries no offset ('yyyy-MM-dd HH:mm:ss', how the
 * booking API sends expirations).
 */
export function parseUtcDateString(value: string) {
  const trimmed = value.trim();

  return parseDateString(hasTimeOffset(trimmed) ? trimmed : `${trimmed}Z`);
}

function toDate(value: Date | string | number) {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);

  return parseDateString(value);
}

/** Day in Rome of an instant (Date, epoch or date string). */
export function romeDayKeyOf(
  value: Date | string | number | null | undefined,
): DayKey | null {
  if (value === null || value === undefined || value === '') return null;
  const date = toDate(value);
  if (!isValid(date)) return null;

  const wall = romeWallClock(date.getTime());

  return formatDayKey(wall.year, wall.month, wall.day);
}

/**
 * Day of a value given in Rome time: a string without an offset is a Rome wall time, so its
 * own date; one with an offset, or a Date, is an instant. Missing means today.
 */
export function romeDayKeyOfRomeTime(
  value: Date | string | null | undefined,
): DayKey | null {
  if (!value) return romeTodayKey();
  if (value instanceof Date) return romeDayKeyOf(value);

  return hasTimeOffset(value) ? romeDayKeyOf(value) : toDayKey(value);
}

/** Today in Rome. */
export function romeTodayKey(): DayKey {
  return romeDayKeyOf(Date.now()) as DayKey;
}

/**
 * Calendar day of a picker Date or an API date string. A Date counts by its local day (the
 * picker builds local midnights); a string by its own 'yyyy-MM-dd' prefix. Converting either
 * as an instant would move the day for visitors far from Rome.
 */
export function toDayKey(
  value: Date | string | null | undefined,
): DayKey | null {
  if (value instanceof Date) {
    return isValid(value)
      ? formatDayKey(value.getFullYear(), value.getMonth() + 1, value.getDate())
      : null;
  }
  if (!value) return null;

  // Also takes 'yyyy-MM-dd HH:mm:ss' (PHP dates)
  const day = value.split('T')[0].match(/^\d{4}-\d{2}-\d{2}/)?.[0];

  return day && isValid(parseISO(day)) ? day : null;
}

export function addDaysToKey(key: DayKey, days: number): DayKey | null {
  if (!Number.isFinite(days)) return null;

  const [year, month, day] = splitDayKey(key);
  const date = new Date(Date.UTC(year, month - 1, day) + days * DAY_MS);

  return formatDayKey(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
}

/** start <= key <= end, by day. False when any day is missing. */
export function isDayKeyBetween(
  key: DayKey | null,
  start: DayKey | null,
  end: DayKey | null,
) {
  if (!key || !start || !end) return false;

  return start <= key && key <= end;
}

/** 'd/M/yyyy' label of a day (key of the nightly price list). */
export function dayKeyToDmy(key: DayKey) {
  const [year, month, day] = splitDayKey(key);

  return `${day}/${month}/${year}`;
}

/** Inverse of dayKeyToDmy. */
export function dmyToDayKey(value: string): DayKey | null {
  const [day, month, year] = value.split('/').map(Number);
  if (!day || !month || !year) return null;

  return formatDayKey(year, month, day);
}

/** First instant of a Rome day. */
export function romeDayStartMs(key: DayKey) {
  const [year, month, day] = splitDayKey(key);

  return wallToEpoch({
    year,
    month,
    day,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}

/** Last millisecond of a Rome day. */
export function romeDayEndMs(key: DayKey) {
  return romeDayStartMs(addDaysToKey(key, 1) as DayKey) - 1;
}

/** Moves an instant by whole days on the Rome wall clock (keeps the time of day). */
export function addRomeDays(epoch: number, days: number) {
  if (!Number.isFinite(epoch) || !Number.isFinite(days)) return NaN;
  const wall = romeWallClock(epoch);
  const [year, month, day] = splitDayKey(
    addDaysToKey(formatDayKey(wall.year, wall.month, wall.day), days) as DayKey,
  );

  return wallToEpoch({ ...wall, year, month, day }, romeOffsetMs(epoch));
}

/**
 * Whole days between two instants on the Rome wall clock (end - start), truncated toward
 * zero.
 */
export function diffRomeDays(endEpoch: number, startEpoch: number) {
  if (!Number.isFinite(endEpoch) || !Number.isFinite(startEpoch)) return NaN;
  const zoneDelta = romeOffsetMs(startEpoch) - romeOffsetMs(endEpoch);
  const days = (endEpoch - startEpoch - zoneDelta) / DAY_MS;

  return Math.trunc(days) || 0;
}

/**
 * Whole days between two local Dates (end - start), truncated toward zero; a DST change in
 * between does not cost a day. NaN when a date is missing.
 */
export function diffLocalDays(end: Date | null, start: Date | null) {
  if (!end || !start) return NaN;

  const zoneDelta =
    (end.getTimezoneOffset() - start.getTimezoneOffset()) * 60_000;
  const days = (end.getTime() - start.getTime() - zoneDelta) / DAY_MS;

  return Math.trunc(days) || 0;
}
